import { useMemo, useState } from 'react';
import type { CharacterResponse, CharacterSummary } from '../types';
import {
  eligibleDungeons,
  eligibleGroupmates,
  groupByExpansion,
  statusLabel,
  type Eligibility,
} from '../lib/dungeons';
import { EXPANSION_LABEL } from '../data/dungeons';
import { classColor } from '../lib/wow';

interface Props {
  /** The currently displayed character (anchor for the suggestions). */
  anchor: CharacterResponse;
  /** All known characters (for cross-/same-account groupmate matching). */
  candidates: CharacterSummary[];
}

const STATUS_DOT: Record<Eligibility, string> = {
  green:  '#5dd96a',
  yellow: '#e6c44a',
  heroic: '#a335ee',
  out:    '#666',
};

const RANK: Record<Eligibility, number> = { green: 0, heroic: 1, yellow: 2, out: 3 };

export function DungeonHelper({ anchor, candidates }: Props) {
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => eligibleDungeons(anchor.level), [anchor.level]);

  // Sort: status priority, then highest tier (= largest min level) first.
  const ordered = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (RANK[a.status] !== RANK[b.status]) return RANK[a.status] - RANK[b.status];
      return b.dungeon.min - a.dungeon.min;
    });
  }, [rows]);

  const visible = showAll ? ordered : ordered.slice(0, 6);

  const grouped = useMemo(() => groupByExpansion(visible), [visible]);

  if (rows.length === 0) {
    return (
      <section className="dungeon-helper">
        <h3>Dungeons</h3>
        <p className="dungeon-helper-empty">
          Level {anchor.level} has no eligible dungeons in the helper. Pop in
          when you hit level 13.
        </p>
      </section>
    );
  }

  return (
    <section className="dungeon-helper">
      <header className="dungeon-helper-header">
        <h3>Dungeons you can run</h3>
        <span className="dungeon-helper-sub">
          based on {anchor.name}, L{anchor.level} · {ordered.length} eligible
        </span>
      </header>

      {grouped.map(({ expansion, rows: erows }) => (
        <div key={expansion} className="dungeon-helper-group">
          <h4 className={`dungeon-helper-group-title exp-${expansion}`}>
            {EXPANSION_LABEL[expansion]}
            <span className="dungeon-helper-group-count">{erows.length}</span>
          </h4>
          <div className="dungeon-helper-grid">
            {erows.map(({ dungeon: d, status }) => {
              const mates = eligibleGroupmates(anchor.key, anchor.owner ?? null, candidates, d);
              const same = mates.filter((m) => m.sameAccount);
              const cross = mates.filter((m) => !m.sameAccount);
              return (
                <article key={d.id} className={`dungeon-card status-${status}`}>
                  <div className="dungeon-card-top">
                    <span className="dungeon-card-dot" style={{ background: STATUS_DOT[status] }} />
                    <a
                      className="dungeon-card-name"
                      href={`https://www.wowhead.com/wotlk/zone=${d.slug}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {d.name}
                    </a>
                    <span className="dungeon-card-status">{statusLabel(status)}</span>
                  </div>
                  <div className="dungeon-card-meta">
                    <span title="Suggested level range (normal mode)">
                      {d.min === d.max ? `Lvl ${d.min}` : `Lvl ${d.min}–${d.max}`}
                    </span>
                    {d.heroicLevel !== null && <span>· Heroic {d.heroicLevel}</span>}
                    <span>· {d.region}</span>
                    {d.note && <span className="dungeon-card-note">· {d.note}</span>}
                  </div>
                  {(same.length > 0 || cross.length > 0) && (
                    <div className="dungeon-card-mates">
                      {same.length > 0 && (
                        <div className="dungeon-mates-row">
                          <span className="dungeon-mates-label">Your alts</span>
                          <ul className="dungeon-mates-list">
                            {same.slice(0, 8).map((m) => (
                              <li key={m.char.key}>
                                <span style={{ color: classColor(m.char.classFile) }}>{m.char.name}</span>
                                <span className="muted"> L{m.char.level}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {cross.length > 0 && (
                        <div className="dungeon-mates-row">
                          <span className="dungeon-mates-label">Friends</span>
                          <ul className="dungeon-mates-list">
                            {cross.slice(0, 8).map((m) => (
                              <li key={m.char.key}>
                                <span style={{ color: classColor(m.char.classFile) }}>{m.char.name}</span>
                                <span className="muted"> L{m.char.level} · {m.char.owner ?? 'other'}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      ))}

      {ordered.length > 6 && (
        <button
          type="button"
          className="dungeon-helper-toggle"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Show top 6' : `Show all ${ordered.length}`}
        </button>
      )}
    </section>
  );
}
